import { FC, RefObject, useCallback, useMemo, useRef, useState } from 'react';
import {
  confirmPanelFormClose,
  Form,
  FormCheckbox,
  FormDateRangePicker,
  FormAlerts,
  FormField,
  FormInput,
  FormInputNumber,
  warningAlgorithmDirty,
  FormRef,
} from '../../common/form';
import { isCreateParam } from '../../common/queryParams/create';
import { useToggleKeys } from '../../common/utils/useToggleKeys';
import { AlgorithmValidFormDataSchema } from '../schema';
import { Button, Checkbox, Divider, Flex } from 'antd';
import styled from 'styled-components';
import { token } from '@atlas-frontend/design-tokens';
import {
  ActionButton,
  Bleed,
  Main,
  ObjectHeader,
  Padding,
  Popconfirm,
  SidePanel,
  Tooltip,
} from '../../common/components';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { AlgorithmAlternativeCell } from './AlgorithmAlternativeCell';
import translations from './AlgorithmForm.translations.json';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { SystemClassifierFormSelect } from '../../configuration/SystemClassifierFormSelect';
import { GroupCodeFormAutoComplete } from '../GroupCodeFormAutoComplete';
import { cleanArray, confirmationHeaders } from '../../common/utils';
import { useDataObjectFormLabelTranslation } from '../useDataObjectFormLabelTranslation';
import {
  GetApiErrorMessage,
  ValidationMessagesMap,
} from '../../common/utils/ValidationMessagesMap';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import {
  DataObjectFormType,
  DataObjectOutputType,
  DataObjectType,
} from '../types';
import { BrokenReferencesAlertWithDrawer } from './brokenReferences/BrokenReferencesAlertWithDrawer';
import { CheckBrokenReferencesButton } from './brokenReferences/CheckBrokenReferencesButton';
import { useBrokenReferences } from './brokenReferences/useBrokenReferences';
import { AlgorithmJoins } from './joins/AlgorithmJoins';
import { useAlgorithmJoinsDataQuery } from './joins/useAlgorithmJoinsDataQuery';
import { AlgorithmJoinsAndMappingsEditModal } from './joinsAndMappings/AlgorithmJoinsAndMappingsEditModal';
import { AlgorithmMappings } from './mappings/AlgorithmMappings';
import { AlgorithmFormSources } from './sources/AlgorithmFormSources';
import { OpenCloseAllBlocksButtons } from './OpenCloseAllBlocksButtons';
import { AlgorithmFormFilterClause } from './filterClause/AlgorithmFormFilterClause';
import { AlgorithmTargetsFormDataType } from './targets/schema/types';
import {
  AlgorithmAlternativeMapping,
  CloneModalState,
  findAlgorithmAlternative,
  initAlgorithmAlternatives,
} from './utils';
import { AlgorithmFormTargets } from './targets/AlgorithmFormTargets';
import { useListAlgorithmsOverviewQuery } from './query/useGetAlgorithmsOverviewQuery';
import {
  AlgorithmFormDataType,
  AlgorithmValidFormDataType,
} from './schema/types';
import { DataObjectDescriptionVersionStore } from '../DataObjectDescriptionVersionStore';
import { useMutationErrors } from '../../common/queryClient';
import validationTranslations from '../translations/validation.translations.json';
import { useMutateAlgorithmQuery } from './query/useMutateAlgorithmQuery';
import { useGetAlgorithmQuery } from './query/useGetAlgorithmQuery';
import { useSystemClassifierItem } from '../../configuration';
import { defaultAlgorithmFormData } from '../schema/defaultValues';
import { useQueryParams } from '../../common/queryParams/useQueryParams';
import { useAlertContext } from '../../common/form/alerts/useAlertContext';
import { AlgorithmCodeAutoComplete } from './AlgorithmCodeAutoComplete';
import { AlgorithmTypeSelect } from './AlgorithmTypeSelect';
import { AlgorithmType } from './schema/zAlgorithmType';
import { TestCalculateAlgorithmsModal } from './testRun/AlgorithmTestCalculateModal';
import { SQL_MAX_LENGTH } from '../../common/constants';

const { addTranslations, t } = i18nNsFactory('algorithm-form');
addTranslations(translations);
addTranslations(validationTranslations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export interface AlgorithmFormProps {
  onCreate?: (id: number) => void;
  onClone?: (algorithmId: number, state: CloneModalState) => void;
  onDelete?: (id: number) => void;
  onClose?: () => void;
  onChangeTargets?: (changedTargets: AlgorithmTargetsFormDataType) => void;
}

export const AlgorithmForm: FC<AlgorithmFormProps> = (props) => {
  const { onCreate, onClone, onClose, onDelete, onChangeTargets } = props;
  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');
  const isInput =
    dataObjectDescriptionVersion.formType === DataObjectFormType.IN;
  const activeStatusId = useSystemClassifierItem(
    'ALGORITHM_STATUS',
    'ACTIVE',
  )?.id;

  const ref: RefObject<FormRef<any> | null> = useRef(null);

  const [{ algorithmId }] = useQueryParams();
  const editableId: number | null = isCreateParam(algorithmId)
    ? null
    : Number(algorithmId);

  const defaultAlgorithm: AlgorithmFormDataType = useMemo(
    () => ({
      ...defaultAlgorithmFormData(
        isInput,
        dataObjectDescriptionVersion.versionId,
      ),
      statusId: activeStatusId!,
    }),
    [activeStatusId, dataObjectDescriptionVersion.versionId, isInput],
  );

  const {
    data: algorithm = defaultAlgorithm,
    isFetching,
    isRefetching,
    refetch,
  } = useGetAlgorithmQuery(
    {
      id: editableId ?? 0,
    },
    {
      enabled: !!editableId,
    },
  );

  const { data: algorithms } = useListAlgorithmsOverviewQuery({
    dataObjectVersionId: dataObjectDescriptionVersion.versionId,
  });

  const { resetAlert, setSuccess } = useAlertContext();
  const { mutateAsync } = useMutateAlgorithmQuery();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);

  const algorithmCodes = useMemo(
    (): string[] =>
      cleanArray(
        algorithms
          ?.filter((item) => item.id !== editableId)
          .map(({ code }) => code),
      ) || [],
    [algorithms, editableId],
  );
  const algorithmGroupCodes = useMemo(
    (): string[] =>
      cleanArray(
        Array.from(new Set(algorithms?.map(({ groupCode }) => groupCode))),
      ) || [],
    [algorithms],
  );

  const [preSaveErrors, setPreSaveErrors] = useState<ValidationMessagesMap>(
    new ValidationMessagesMap(),
  );

  /**
   * Checks if:
   *  - algorithm's `groupCode` clashes with its own `code` or with other existing algorithm's `code` values.
   *  - algorithm's `code` clashes with other existing algorithm's `groupCode` values.
   *  returns `true` if no clashes found, otherwise sets form-external errors (serverErrors) and returns `false`.
   */
  const codesDoNotClash = useCallback(
    (validFormData: AlgorithmValidFormDataType) => {
      const setError = (
        fieldName: string,
        messageKey = 'must-not-clash-with-algorithm-codes',
      ) => {
        const messagesMap = new ValidationMessagesMap();
        messagesMap.append(
          fieldName,
          t(`validation.${fieldName}.${messageKey}`),
          'error',
        );
        setPreSaveErrors(messagesMap);
        return false;
      };

      if (
        validFormData.groupCode &&
        (validFormData.groupCode === validFormData.code ||
          algorithmCodes.includes(validFormData.groupCode))
      ) {
        return setError('groupCode');
      } else if (
        algorithmCodes
          .filter((c) => c !== dataObjectDescriptionVersion.code)
          .includes(validFormData.code)
      ) {
        return setError('code');
      } else if (algorithmGroupCodes.includes(validFormData.code)) {
        return setError('code', 'must-not-clash-with-group-values');
      }

      setPreSaveErrors(new ValidationMessagesMap());
      return true;
    },
    [
      algorithmCodes,
      algorithmGroupCodes,
      setPreSaveErrors,
      dataObjectDescriptionVersion,
    ],
  );

  // used to update form key to reset internal form state (isDirty etc) after successful save
  const [saveCount, setSaveCount] = useState(0);
  const handleSubmit = useCallback(
    (validFormData: AlgorithmValidFormDataType, confirm: boolean) => {
      if (codesDoNotClash(validFormData)) {
        resetAlert();
        clearMutationErrors();
        return mutateAsync({
          id: editableId ?? undefined,
          data: validFormData,
          headers: confirmationHeaders(confirm),
        })
          .then(async (response) => {
            ref?.current?.form.reset(undefined, {
              keepValues: true,
              keepDirty: false,
            });
            if (!editableId) {
              onCreate?.((response as AlgorithmFormDataType).id);
              setSuccess(t('alert.created'));
            } else {
              await refetch();
              setSaveCount((n) => n + 1);
              setSuccess(t('alert.success'));
            }
          })
          .catch((error) => {
            setMutationErrors(error?.response?.data?.errors ?? []);
          });
      }
    },
    [
      codesDoNotClash,
      resetAlert,
      clearMutationErrors,
      mutateAsync,
      editableId,
      onCreate,
      setSuccess,
      refetch,
      setMutationErrors,
    ],
  );

  if (!algorithm || (isFetching && !isRefetching)) {
    return null;
  }

  return (
    <Form
      schema={AlgorithmValidFormDataSchema}
      key={`${editableId ?? 'new'}-${saveCount}`}
      defaultValues={algorithm}
      onSubmit={handleSubmit}
      serverErrors={!mutationErrors.size ? preSaveErrors : mutationErrors}
      idAttributePrefix={`algorithm`}
      formRef={ref}
    >
      <AlgorithmFormInner
        algorithm={algorithm}
        editableId={editableId || undefined}
        existingAlgorithmCodes={algorithmCodes}
        existingAlgorithmGroupCodes={algorithmGroupCodes}
        onClone={onClone}
        onClose={onClose}
        onDelete={() => onDelete?.(editableId || 0)}
        isCreate={!editableId}
        defaultIsDbScriptEnabled={algorithm?.expression !== ''}
        onChangeTargets={onChangeTargets}
      />
    </Form>
  );
};

const blockKeys = ['sources', 'mappings', 'joins', 'filterClause', 'targets'];

interface AlgorithmFormInnerProps {
  algorithm: AlgorithmFormDataType;
  editableId?: number;
  existingAlgorithmCodes: string[];
  existingAlgorithmGroupCodes: string[];
  onClone?: (algorithmId: number, state: CloneModalState) => void;
  onClose?: () => void;
  onDelete?: () => void;
  isCreate: boolean;
  defaultIsDbScriptEnabled: boolean;
  onChangeTargets?: (changedTargets: AlgorithmTargetsFormDataType) => void;
}

const AlgorithmFormInner: FC<AlgorithmFormInnerProps> = (props) => {
  const {
    algorithm,
    editableId,
    existingAlgorithmCodes,
    existingAlgorithmGroupCodes,
    onClone,
    onClose,
    onDelete,
    isCreate,
    defaultIsDbScriptEnabled,
    onChangeTargets,
  } = props;
  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');
  const isInput =
    dataObjectDescriptionVersion.formType === DataObjectFormType.IN;
  const { t: tLabel } = useDataObjectFormLabelTranslation();
  const { setValue } = useFormContext();
  const { isDirty, errors } = useFormState();
  const { resetAlert, setSuccess } = useAlertContext();
  const [testRunModalOpen, setTestRunModalOpen] = useState(false);
  const isNew = !algorithm || !algorithm.id || algorithm.id < 0;
  const disableActionButtons = isNew || isDirty;
  const handleCancel = useCallback(() => {
    if (isDirty) {
      confirmPanelFormClose(onClose);
    } else {
      onClose?.();
    }
  }, [isDirty, onClose]);

  const [{ targetId }] = useQueryParams();

  const [isDbScriptEnabled, setIsDbScriptEnabled] = useState(
    defaultIsDbScriptEnabled,
  );

  const [id, code, typeCode] = useWatch({ name: ['id', 'code', 'typeCode'] });
  const handleDbScriptToggle = useCallback(
    (event: CheckboxChangeEvent) => {
      const opts = { shouldDirty: true };
      if (event.target.checked) {
        setValue('aggregatePeriods', null, opts);
        setIsDbScriptEnabled(true);
      } else {
        if (!isInput) {
          setValue('aggregatePeriods', true, opts);
        }
        setValue('expression', '', opts);
        setIsDbScriptEnabled(false);
      }
    },
    [isInput, setValue],
  );

  const showExecAfterAggregation = useMemo(
    () =>
      dataObjectDescriptionVersion.outputTypeCode !==
        DataObjectOutputType.REPORT &&
      Boolean(
        dataObjectDescriptionVersion.dimensions?.some(
          (d) => d.aggregateHierarchy,
        ),
      ),
    [dataObjectDescriptionVersion],
  );

  const showCloneModal = useCallback(
    (finish: boolean) => {
      if (isDirty) {
        warningAlgorithmDirty();
      } else {
        onClone?.(
          id,
          finish ? CloneModalState.CLONE_FINISH : CloneModalState.CLONE,
        );
      }
    },
    [id, isDirty, onClone],
  );

  const {
    toggle: toggleBlock,
    isEnabled: isBlockExpanded,
    clear: closeAllBlocks,
    fill: openBlocks,
  } = useToggleKeys(blockKeys);

  const [isJoinsAndMappingsModalOpen, setIsJoinsAndMappingsModalOpen] =
    useState<'joins' | 'mappings' | 'graph' | null>(null);

  const openModal = useMemo(() => {
    if (isCreate || isDirty) {
      const warn = () => warningAlgorithmDirty();
      return { mappings: warn, joins: warn, graph: warn };
    } else {
      return {
        mappings: () => setIsJoinsAndMappingsModalOpen('mappings'),
        joins: () => setIsJoinsAndMappingsModalOpen('joins'),
        graph: () => setIsJoinsAndMappingsModalOpen('graph'),
      };
    }
  }, [isCreate, isDirty]);

  const {
    data: algorithmJoinsAndMappingsData,
    isFetching: isAlgorithmJoinsAndMappingsDataFetching,
    refetchJoins,
    refetchMappings,
  } = useAlgorithmJoinsDataQuery(algorithm.id);

  const handleChangeMappingsAndJoins = useCallback(
    (changedTypes: ('joins' | 'mappings')[]) => {
      if (changedTypes.includes('joins')) {
        void refetchJoins();
      }
      if (changedTypes.includes('mappings')) {
        void refetchMappings();
      }
    },
    [refetchJoins, refetchMappings],
  );

  const alternativeMaps = useMemo(
    () =>
      initAlgorithmAlternatives(
        dataObjectDescriptionVersion.alternativeAlgorithmSets ?? [],
      ),
    [dataObjectDescriptionVersion],
  );

  const { brokenReferencesMap, refetch, isFetching } = useBrokenReferences([
    editableId || 0,
  ]);

  const handleCheckBrokenReferences = useCallback(async () => {
    resetAlert();
    const { data } = await refetch();
    if (data?.errors.length === 0) {
      setSuccess(t('alert.no-broken-references'));
    }
  }, [resetAlert, refetch, setSuccess]);

  return (
    <SidePanel
      minWidth={800}
      collapsedWidth={800}
      side="right"
      width={1786}
      mode="modal"
    >
      <SidePanelWrapper>
        <FormAlerts view={'algorithm'} />
        <BrokenReferencesAlertWithDrawer
          brokenReferencesMap={brokenReferencesMap}
          tLabel={tLabel}
        />
        <ScrollContent>
          <Flex vertical gap="large">
            <AlgorithmFormHeader isCreate={isCreate} />
            <FlexWithItemsStretched vertical gap="middle">
              <Flex gap="small">
                <FormField
                  name="typeCode"
                  label={tLabel('algorithms.type.label')}
                  required
                  preview={
                    !isCreate && {
                      previewOnly: true,
                      render: (value) => tLabel('algorithms.type.' + value),
                    }
                  }
                >
                  <AlgorithmTypeSelect name="typeCode" />
                </FormField>
              </Flex>
              <Flex gap="small">
                <FormField
                  name="code"
                  label={tLabel('algorithms.code.label')}
                  required
                >
                  <AlgorithmCodeAutoComplete
                    name="code"
                    algorithmCodes={existingAlgorithmCodes}
                  />
                </FormField>
                {typeCode === AlgorithmType.INSERT && (
                  <>
                    <FormField
                      name="groupCode"
                      label={tLabel('algorithms.groupCode.label')}
                    >
                      <GroupCodeFormAutoComplete
                        name="groupCode"
                        groupCodes={existingAlgorithmGroupCodes}
                      />
                    </FormField>
                    {dataObjectDescriptionVersion?.dataObjectType ===
                      DataObjectType.OUT && (
                      <AlternativeFormField
                        name="alternative"
                        label={tLabel('algorithms.alternative.label')}
                      >
                        <AlgorithmFormAlternative
                          alternatives={alternativeMaps}
                        />
                      </AlternativeFormField>
                    )}
                  </>
                )}
                <FormField
                  name="sequenceNumber"
                  label={tLabel('algorithms.sequenceNumber.label')}
                  required
                >
                  <SequenceNumberFormInputNumber name="sequenceNumber" />
                </FormField>
                <FormField
                  name="validPeriod"
                  label={tLabel('algorithms.validPeriod.label')}
                >
                  <FormDateRangePicker
                    name="validPeriod"
                    allowEmpty={[true, true]}
                  />
                </FormField>
              </Flex>
              <Flex gap="small">
                <FormField
                  name="statusId"
                  label={tLabel('algorithms.status.label')}
                >
                  <StyledStatusFormSelect
                    name="statusId"
                    code="ALGORITHM_STATUS"
                  />
                </FormField>
                <FormField
                  name="description"
                  label={tLabel('algorithms.description.label')}
                  style={{ flexGrow: 1 }}
                >
                  <FormInput name="description" rows={1} maxLength={2000} />
                </FormField>
              </Flex>
            </FlexWithItemsStretched>
            <Flex gap="middle">
              {!isInput && typeCode === AlgorithmType.INSERT && (
                <>
                  <Flex vertical>
                    <Checkbox
                      id="enable-db-script"
                      checked={isDbScriptEnabled}
                      onChange={handleDbScriptToggle}
                    >
                      {t('enable-db-script.label')}
                    </Checkbox>
                  </Flex>
                  {!isDbScriptEnabled && (
                    <FormField name="aggregatePeriods">
                      <FormCheckbox name="aggregatePeriods">
                        {tLabel('algorithms.aggregatePeriods.label')}
                      </FormCheckbox>
                    </FormField>
                  )}
                </>
              )}
            </Flex>
            <Flex
              gap="middle"
              justify={showExecAfterAggregation ? 'space-between' : 'end'}
            >
              {showExecAfterAggregation && (
                <FormField name="execAfterAggregation">
                  <FormCheckbox name="execAfterAggregation">
                    {tLabel('algorithms.execAfterAggregation.label')}
                  </FormCheckbox>
                </FormField>
              )}
              {!isDbScriptEnabled && (
                <OpenCloseAllBlocksButtons
                  onClickOpen={openBlocks}
                  onClickClose={closeAllBlocks}
                />
              )}
            </Flex>
            <AlgorithmFormSources
              isAlgorithmDirty={isCreate || isDirty}
              expanded={isBlockExpanded('sources')}
              onToggle={() => toggleBlock('sources')}
            />
            {!isDbScriptEnabled && (
              <>
                <AlgorithmMappings
                  algorithmId={algorithm.id}
                  expanded={isBlockExpanded('mappings')}
                  onToggle={() => toggleBlock('mappings')}
                  mappings={algorithmJoinsAndMappingsData?.mappings}
                  isLoading={isAlgorithmJoinsAndMappingsDataFetching}
                  onEditButtonClick={openModal.mappings}
                  onEditGraphClick={openModal.graph}
                  hasError={Boolean(errors.mappings)}
                />
                {isJoinsAndMappingsModalOpen !== null &&
                  algorithmJoinsAndMappingsData && (
                    <AlgorithmJoinsAndMappingsEditModal
                      isModalOpen={isJoinsAndMappingsModalOpen}
                      algorithmId={algorithm.id}
                      mappings={algorithmJoinsAndMappingsData.mappings}
                      joins={algorithmJoinsAndMappingsData.joins}
                      sources={algorithmJoinsAndMappingsData.sources}
                      sourceDimensionsMap={
                        algorithmJoinsAndMappingsData.sourceDimensionsMap
                      }
                      onClose={() => setIsJoinsAndMappingsModalOpen(null)}
                      onSwitchModalType={setIsJoinsAndMappingsModalOpen}
                      onChange={handleChangeMappingsAndJoins}
                    />
                  )}
                {typeCode === AlgorithmType.INSERT && (
                  <AlgorithmJoins
                    algorithmId={algorithm.id}
                    expanded={isBlockExpanded('joins')}
                    onToggle={() => toggleBlock('joins')}
                    joins={algorithmJoinsAndMappingsData?.joins}
                    sources={algorithmJoinsAndMappingsData?.sources}
                    isLoading={isAlgorithmJoinsAndMappingsDataFetching}
                    onEditButtonClick={openModal.joins}
                    onEditGraphClick={openModal.graph}
                    hasError={Boolean(errors.joins)}
                  />
                )}
                <AlgorithmFormFilterClause
                  isAlgorithmDirty={isCreate || isDirty}
                  expanded={isBlockExpanded('filterClause')}
                  onToggle={() => toggleBlock('filterClause')}
                />
                <AlgorithmFormTargets
                  isAlgorithmDirty={isCreate || isDirty}
                  targetId={
                    targetId !== undefined ? Number(targetId) : undefined
                  }
                  expanded={
                    targetId !== undefined || isBlockExpanded('targets')
                  }
                  onToggle={() => toggleBlock('targets')}
                  onChange={onChangeTargets}
                />
              </>
            )}
            {isDbScriptEnabled && (
              <>
                <Bleed direction="horizontal">
                  <Divider />
                </Bleed>
                <FormField
                  name="expression"
                  label={tLabel('algorithms.expression.label')}
                >
                  <FormInput
                    name="expression"
                    rows={3}
                    maxLength={SQL_MAX_LENGTH}
                    style={{ fontFamily: 'monospace' }}
                  />
                </FormField>
              </>
            )}
          </Flex>
        </ScrollContent>
        <Main.Footer>
          <FullWidthFlex gap="small">
            <Tooltip title={isNew ? t('tooltip.save') : ''}>
              <Popconfirm
                id={`algorithmForm-delete-confirm`}
                title={t('confirm.title')}
                description={t('confirm.description')}
                okText={t('button.yes')}
                cancelText={t('button.no')}
                onConfirm={onDelete}
              >
                <Button
                  id="algorithmForm-delete"
                  disabled={alternativeMaps.algorithmMap.has(code) || isNew}
                >
                  {t('button.delete')}
                </Button>
              </Popconfirm>
            </Tooltip>
            <Tooltip title={disableActionButtons ? t('tooltip.save') : ''}>
              <Button
                id="algorithmForm-clone"
                onClick={() => showCloneModal(false)}
                disabled={disableActionButtons}
              >
                {t('button.clone')}
              </Button>
            </Tooltip>
            <Tooltip title={disableActionButtons ? t('tooltip.save') : ''}>
              <Button
                id="algorithmForm-clone-finish"
                onClick={() => showCloneModal(true)}
                disabled={disableActionButtons}
              >
                {t('button.cloneFinish')}
              </Button>
            </Tooltip>
          </FullWidthFlex>
          <FullWidthFlex justify="end" gap="small">
            {dataObjectDescriptionVersion?.dataObjectType ===
              DataObjectType.OUT && (
              <Tooltip title={disableActionButtons ? t('tooltip.save') : ''}>
                <Button
                  id="algorithmForm-testRun"
                  onClick={() => setTestRunModalOpen(true)}
                  disabled={disableActionButtons}
                >
                  {t('button.testRun')}
                </Button>
              </Tooltip>
            )}
            <Tooltip title={disableActionButtons ? t('tooltip.save') : ''}>
              <CheckBrokenReferencesButton
                onClick={handleCheckBrokenReferences}
                loading={isFetching}
                disabled={disableActionButtons}
              />
            </Tooltip>
            <ActionButton type="abort" onClick={handleCancel} />
            <ActionButton type="formSubmit" />
          </FullWidthFlex>
        </Main.Footer>
      </SidePanelWrapper>
      {testRunModalOpen && (
        <TestCalculateAlgorithmsModal
          algorithms={[algorithm]}
          onClose={() => setTestRunModalOpen(false)}
        />
      )}
    </SidePanel>
  );
};

const AlgorithmFormHeader: FC<{ isCreate: boolean }> = (props) => {
  const { isCreate } = props;
  const code = useWatch({ name: 'code' });
  return (
    <ObjectHeader
      upperText={code}
      lowerText={t(`heading.${isCreate ? 'create' : 'edit'}`)}
    />
  );
};

const AlgorithmFormAlternative: FC<{
  alternatives: AlgorithmAlternativeMapping;
}> = ({ alternatives }) => {
  const [code, groupCode] = useWatch({ name: ['code', 'groupCode'] });
  const alternative = useMemo(
    () => findAlgorithmAlternative(code, groupCode, alternatives),
    [alternatives, code, groupCode],
  );

  return (
    <Flex align="center">
      <AlgorithmAlternativeCell alternativeSet={alternative} />
    </Flex>
  );
};

const SequenceNumberFormInputNumber = styled(FormInputNumber)`
  min-width: 8rem;
`;

const FullWidthFlex = styled(Flex)`
  width: 100%;
`;

const SidePanelWrapper = styled.div<{ $hasBanner?: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
  border-left: 1px solid ${token.colorBorderSecondary};
`;

const FlexWithItemsStretched = styled(Flex)`
  align-items: stretch;
`;

const AlternativeFormField = styled(FormField)`
  > div {
    height: 100%;
  }
`;

const StyledStatusFormSelect = styled(SystemClassifierFormSelect)`
  min-width: 10rem;
`;

const ScrollContent = styled(Padding)`
  overflow-y: auto;
  flex: 1;
`;
